"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SelectInputComponent = exports.SelectInputState = exports.Style = exports.CSS = void 0;
const tslib_1 = require("tslib");
const mobx_1 = require("mobx");
const i18n_1 = require("../../i18n");
const mithril_1 = require("../../mithril");
const theme_1 = require("../theme");
const input_state_1 = require("./input.state");
const services_1 = require("../../services");
exports.CSS = {
    container: 'c-Select',
    buttonContainer: 'c-Select__container',
    option: 'c-Select__option',
    label: 'c-Select__label',
    button: 'c-Select__button',
    error: 'c-Select__error',
    buttonDropDown: 'c-Select__drop',
    buttonText: 'c-Select__text',
    native: 'c-Select__native',
    isFullWidth: 'c-Select--full-width',
    isFocused: 'c-Select--is-focused',
    isDirty: 'c-Select--is-dirty',
    isDisabled: 'c-Select--is-disabled',
    isInvalid: 'c-Select--is-invalid',
    isOpen: 'c-Select--is-open',
    isFilled: 'c-Select--is-filled',
    isEmpty: 'c-Select--is-empty',
    MDL: {
        error: 'mdl-textfield__error',
        label: 'mdl-textfield__label',
        labelFloating: 'mdl-textfield--floating-label',
        button: 'mdl-button',
        isFocused: 'is-focused',
        isDirty: 'is-dirty',
        isUpgraded: 'is-upgraded',
        isInvalid: 'is-invalid',
        hasPlaceholder: 'has-placeholder',
    },
};
const padding = (top, right, bottom, left) => `${theme_1.DefaultTheme.space(top)} ${theme_1.DefaultTheme.space(right)} ${theme_1.DefaultTheme.space(bottom)} ${theme_1.DefaultTheme.space(left)}`;
exports.Style = {
    [exports.CSS.container]: {
        'background-image': `url("data:image/svg+xml;charset=utf-8,%3Csvg width='10' height='5' viewBox='7 10 10 5' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='%230' fill-rule='evenodd' opacity='.54' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
        'background-repeat': 'no-repeat',
        'box-sizing': 'border-box',
        'background-position': 'right 8px bottom 16px',
        position: 'relative',
        width: '300px',
        'max-width': '100%',
        height: '56px',
    },
    [exports.CSS.container + ' .' + exports.CSS.label]: {
        color: theme_1.DefaultTheme.font.color.disabled,
        'text-overflow': 'ellipsis',
        'font-weight': 'normal',
    },
    [exports.CSS.native]: {
        'background-color': 'transparent',
        'font-family': theme_1.DefaultTheme.font.family,
        color: theme_1.DefaultTheme.font.color.supporting,
        outline: 'none',
        'white-space': 'nowrap',
        cursor: 'pointer',
        border: 'none',
        'border-bottom': '1px solid ' + theme_1.DefaultTheme.font.color.divider,
        padding: padding(6, 6, 1, 0),
        'line-height': '1.75rem',
        'font-size': '1rem',
        appearance: 'none', // Hide drop-down button
        width: '100%',
        '&::-ms-expand': {
            display: 'none',
        },
    },
    [exports.CSS.isDisabled]: {
        cursor: 'default',
        pointerEvents: 'none',
        opacity: 0.3,
    },
};
(0, theme_1.addStyles)(exports.Style);
class SelectInputState extends input_state_1.InputState {
    constructor(label, options = []) {
        super(label);
        this.options = mobx_1.observable.array(options, { deep: false });
        // If "isRequired" is enabled later, select the first option by default.
        this.reaction(() => this.isRequired, () => {
            var _a;
            if (this.value) {
                // An option is already selected.
                return;
            }
            if (!((_a = this.options) === null || _a === void 0 ? void 0 : _a[0])) {
                // There are no options, but isRequired is true -- this is an error.
                services_1.Logger.error(this, 'SelectInputState has required=true but no options are set');
                return;
            }
            this.setValue(this.options[0].value);
        });
    }
    // When resetting a select input with isRequired, we need to select the first option again.
    reset() {
        super.reset();
        if (this.isRequired && this.options[0]) {
            this.setValue(this.options[0].value);
        }
    }
    addOption(option) {
        this.options.push(option);
    }
    setOptions(options) {
        this.options.replace(options);
    }
}
exports.SelectInputState = SelectInputState;
tslib_1.__decorate([
    mobx_1.action
], SelectInputState.prototype, "reset", null);
tslib_1.__decorate([
    mobx_1.action
], SelectInputState.prototype, "addOption", null);
tslib_1.__decorate([
    mobx_1.action
], SelectInputState.prototype, "setOptions", null);
exports.SelectInputComponent = {
    view(state) {
        let className = exports.CSS.container + ' ' + exports.CSS.MDL.labelFloating;
        if (state.placeholder || state.isRequired) {
            className += ' ' + exports.CSS.MDL.hasPlaceholder;
        }
        if (state.isFilled) {
            className += ' ' + exports.CSS.isFilled;
            className += ' ' + exports.CSS.MDL.isDirty;
        }
        else {
            className += ' ' + exports.CSS.isEmpty;
        }
        if (state.focused) {
            className += ' ' + exports.CSS.isFocused;
            className += ' ' + exports.CSS.MDL.isFocused;
        }
        if (state.disabled) {
            className = `${className} ${exports.CSS.isDisabled}`;
        }
        if (state.touched && !state.isValid) {
            className += ' ' + exports.CSS.isInvalid;
            className += ' ' + exports.CSS.MDL.isInvalid;
        }
        const EMPTY_VALUE = state.id + '-empty';
        const options = !state.isRequired ? [(0, mithril_1.m)('option', { selected: true, value: EMPTY_VALUE }, state.placeholder)] : [];
        for (const opt of state.options) {
            options.push((0, mithril_1.m)('option', { className: exports.CSS.option, value: opt.value, selected: opt.value === state.value }, opt.key));
        }
        const children = [];
        if (state.isLabelShown) {
            children.push((0, mithril_1.m)('label', { className: exports.CSS.label + ' ' + exports.CSS.MDL.label, title: (0, i18n_1.Translate)(state.label), for: state.id }, (0, i18n_1.Translate)(state.label)));
        }
        children.push((0, mithril_1.m)('select', {
            id: state.id,
            className: exports.CSS.native,
            onchange: (e) => {
                const target = e.target;
                const value = target.value;
                if (value === EMPTY_VALUE) {
                    state.setValue(null);
                }
                else {
                    state.setValue(value);
                }
            },
            onfocus: () => state.setFocused(true),
            onblur: () => {
                state.setFocused(false);
                state.setTouched();
            },
            disabled: state.disabled,
        }, options));
        // Show error message when touched and invalid
        if (state.touched && !state.isValid && state.hasError()) {
            children.push((0, mithril_1.m)('span', { className: exports.CSS.error + ' ' + exports.CSS.MDL.error }, (0, i18n_1.Translate)(state.error)));
        }
        return (0, mithril_1.m)('div', { className }, children);
    },
};
//# sourceMappingURL=input.select.js.map